import type { BudgetLine, BudgetLineType } from '@/engines/budget'

import type { CommercialItem, CommercialItemType, CommitCommercialOptions } from '../types'

function mapCommercialTypeToBudgetLineType(type: CommercialItemType): BudgetLineType {
  switch (type) {
    case 'Service':
      return 'service'
    case 'Software':
    case 'License':
      return 'other'
    default:
      return 'product'
  }
}

function lineDraft(
  input: Omit<BudgetLine, 'id' | 'position'> & { id?: string; position?: number },
): Omit<BudgetLine, 'id' | 'position'> & { id?: string; position?: number } {
  return input
}

/**
 * Maps CommercialItem → Budget Engine AddLine payload(s).
 * No pricing rules beyond field projection — totals belong to Budget Engine.
 */
export function commercialItemToBudgetLines(
  item: CommercialItem,
  options: CommitCommercialOptions = {},
): Array<Omit<BudgetLine, 'id' | 'position'> & { id?: string; position?: number }> {
  const mode = options.mode ?? 'summary'

  if (item.type === 'Computer' && mode === 'expanded' && item.components?.length) {
    const lines = []
    lines.push(
      lineDraft({
        type: 'note',
        description: `${item.name} — configuração`,
        quantity: 0,
        unitCost: 0,
        unitPrice: 0,
        discountPercent: 0,
        discountValue: 0,
        origin: {
          source: 'template',
          productId: item.id,
          externalRef: item.origin.templateId,
        },
        meta: {
          commercialType: 'Computer',
          commercialItemId: item.id,
          expandMode: 'header',
        },
      }),
    )
    for (const c of item.components) {
      lines.push(
        lineDraft({
          type: 'product',
          description: `${c.label} (${c.slot})`,
          quantity: c.quantity * item.quantity,
          unitCost: c.unitCost,
          unitPrice: c.unitPrice,
          discountPercent: 0,
          discountValue: 0,
          origin: {
            source: 'template',
            productId: c.catalogId,
            fingerprint: `commercial:${item.id}:${c.slot}`,
            externalRef: item.id,
          },
          meta: {
            commercialType: 'Computer',
            commercialItemId: item.id,
            slot: c.slot,
            brand: c.brand,
            model: c.model,
          },
        }),
      )
    }
    return lines
  }

  // Summary (default): one commercial line — Computer sold as configuration package
  return [
    lineDraft({
      type: mapCommercialTypeToBudgetLineType(item.type),
      description: item.description ? `${item.name} — ${item.description}` : item.name,
      quantity: item.quantity,
      unitCost: item.unitCost,
      unitPrice: item.unitPrice,
      discountPercent: 0,
      discountValue: 0,
      origin: {
        source: item.origin.source === 'catalog' ? 'manual' : 'template',
        productId: item.origin.catalogId ?? item.id,
        fingerprint: item.origin.fingerprint ?? `commercial:${item.id}`,
        externalRef: item.origin.templateId,
      },
      meta: {
        commercialType: item.type,
        commercialItemId: item.id,
        ...(item.components
          ? {
              componentCount: String(item.components.length),
              bom: item.components.map((c) => `${c.slot}:${c.catalogId}`).join('|'),
            }
          : {}),
        ...(item.meta ?? {}),
      },
    }),
  ]
}
